import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BrandingAssetKind,
  RestoreMode,
  SubscriptionState,
  TenantBranding,
  UUID,
} from '@4zen/api-client';
import { api } from '@/lib/api';

export function useOrganization() {
  return useQuery({
    queryKey: ['organization'],
    queryFn: () => api.admin.organization(),
    staleTime: 10 * 60_000,
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.admin.updateOrganization(body),
    onSuccess: (settings) => queryClient.setQueryData(['organization'], settings),
  });
}

export function useUpdateBranding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TenantBranding) => api.admin.updateBranding(body),
    onSuccess: (settings) => queryClient.setQueryData(['organization'], settings),
  });
}

export function useUploadBrandingAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, file }: { kind: BrandingAssetKind; file: File }) =>
      api.admin.uploadBrandingAsset(kind, file),
    onSuccess: (settings) => queryClient.setQueryData(['organization'], settings),
  });
}

/**
 * The licence, refetched on a slow beat.
 *
 * Every signed-in person reads it — the expiry banner is for the whole team,
 * not only the owner — and a workspace that quietly lapses at midnight should
 * say so without a reload.
 */
export function useSubscription() {
  return useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.admin.subscription(),
    staleTime: 5 * 60_000,
    refetchInterval: 30 * 60_000,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: () => api.admin.plans(),
    staleTime: Infinity,
  });
}

/** Both paths invalidate everything: a new plan changes what the UI may show. */
function useLicenceMutation<TVars>(mutationFn: (vars: TVars) => Promise<SubscriptionState>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (state) => {
      queryClient.setQueryData(['subscription'], state);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

export function useApplyLicense() {
  return useLicenceMutation((key: string) => api.admin.applyLicense(key));
}

export function useUpdateSubscription() {
  return useLicenceMutation((body: { tariff?: string; seats_purchased?: number }) =>
    api.admin.updateSubscription(body),
  );
}

export function useAuditEvents(filters: Record<string, string | number | undefined> = {}) {
  return useQuery({
    queryKey: ['audit', filters],
    queryFn: () => api.admin.audit(filters),
    placeholderData: (previous) => previous,
  });
}

export function useAuditIntegrity() {
  return useQuery({ queryKey: ['audit', 'integrity'], queryFn: () => api.admin.auditIntegrity() });
}

export function useDeletionRequests(status = 'pending') {
  return useQuery({
    queryKey: ['deletion-requests', status],
    queryFn: () => api.admin.deletionRequests(status),
  });
}

export function useReviewDeletion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approve, note }: { id: UUID; approve: boolean; note?: string }) =>
      api.admin.reviewDeletion(id, approve, note),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deletion-requests'] }),
  });
}

export function useSyncConflicts(status = 'pending') {
  return useQuery({
    queryKey: ['sync-conflicts', status],
    queryFn: () => api.sync.conflicts(status || undefined),
  });
}

export function useResolveConflict() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      resolution,
      merged,
    }: {
      id: UUID;
      resolution: 'server' | 'client' | 'merge';
      merged?: Record<string, unknown>;
    }) => api.sync.resolveConflict(id, { resolution, merged }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sync-conflicts'] }),
  });
}

export function useBackups() {
  return useQuery({ queryKey: ['backups'], queryFn: () => api.admin.backups() });
}

export function useCreateBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.admin.createBackup(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backups'] }),
  });
}

export function useRestoreBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mode }: { id: UUID; mode: RestoreMode }) => api.admin.restore(id, mode),
    // A restore rewrites the tenant's rows wholesale, so nothing cached survives it.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}
